// Screen stack, focus management and keyboard/gamepad menu navigation (§27.1). Screens are a
// stack: `replace` sets a new root, `show` pushes (modals keep the screen below visible behind a
// backdrop, overlays keep it visible and live), `back` pops and restores the focus the screen
// had. `input:nav` drives spatial navigation by DOM rect proximity; ←/→ on a value control
// (`data-adjust`) and ↑/↓ on a letter spinner (`data-spin`) change the value instead; confirm
// clicks the focused control (gamepad A).
import type { Bus } from '../core/bus';
import type { LiveScreen, NavAction, ScreenId } from './contracts';
import { ADJUST_EVENT, SPIN_EVENT } from './widgets';
import { reducedMotion, spring } from './ui';

/** A closed modal plays its exit (§11.5: panels pop away, they do not just vanish) this long. */
const CLOSE_MS = 170;

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]';

/** Focusable controls of `scope` in DOM order (roving `tabindex="-1"` options excluded). */
function focusables(scope: HTMLElement): HTMLElement[] {
  return [...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => n.tabIndex >= 0 && visible(n));
}

interface Entry { id: ScreenId; screen: LiveScreen; lastFocus: HTMLElement | null }

export interface ScreenManagerHooks {
  /** Menu sounds: focus moves, back, errors. */
  sound?(id: 'hover' | 'back'): void;
}

function visible(n: HTMLElement): boolean {
  if (n.offsetParent === null && getComputedStyle(n).position !== 'fixed') return false;
  const r = n.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

export class ScreenManager {
  private readonly screens = new Map<ScreenId, LiveScreen>();
  private readonly stack: Entry[] = [];
  /** Modals playing their exit, with the timer that hides them. */
  private readonly closing = new Map<HTMLElement, number>();

  constructor(private readonly root: HTMLElement, private readonly bus: Bus, private readonly hooks: ScreenManagerHooks = {}) {
    // A clicked button on an in-world screen must not keep focus, or the arrows stop moving the bean.
    root.addEventListener('click', (e) => {
      const active = document.activeElement;
      if (e.target instanceof Element && e.target.closest('[data-nofocus]') && !e.target.closest('[data-keepfocus]') && active instanceof HTMLElement && root.contains(active)) active.blur();
    });
    bus.on('input:nav', ({ action }) => this.nav(action));
    // Tab cycles inside the top screen: a modal never lets focus wander to the screen it covers.
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
      const top = this.topEntry();
      if (!top || top.screen.el.hidden) return;
      const scope = top.screen.el;
      // In-world screens without UI focus: Tab is the scoreboard / "focus the panel" key.
      if (scope.hasAttribute('data-nofocus') && !this.focusedIn(scope)) return;
      const items = focusables(scope);
      if (items.length === 0) return;
      e.preventDefault();
      const active = document.activeElement instanceof HTMLElement ? items.indexOf(document.activeElement) : -1;
      const next = e.shiftKey ? (active <= 0 ? items.length - 1 : active - 1) : (active + 1) % items.length;
      this.focus(items[next]);
    }, true);
  }

  register(id: ScreenId, screen: LiveScreen): void {
    this.screens.set(id, screen);
  }

  get current(): ScreenId {
    return this.stack.length > 0 ? this.stack[this.stack.length - 1].id : 'boot';
  }

  get depth(): number {
    return this.stack.length;
  }

  has(id: ScreenId): boolean {
    return this.stack.some((e) => e.id === id);
  }

  /** The root screen (bottom of the stack). */
  get rootId(): ScreenId | null {
    return this.stack.length > 0 ? this.stack[0].id : null;
  }

  /** Screen ids from the root up. */
  ids(): ScreenId[] {
    return this.stack.map((e) => e.id);
  }

  /** Removes `id` from the stack wherever it is (an overlay that no longer applies). */
  remove(id: ScreenId): void {
    const at = this.stack.findIndex((e) => e.id === id);
    if (at <= 0) return;
    if (at === this.stack.length - 1) {
      this.back();
      return;
    }
    const [entry] = this.stack.splice(at, 1);
    entry.screen.el.hidden = true;
    entry.screen.onHide?.();
  }

  /** Clears the stack and shows `id` as the root screen. */
  replace(id: ScreenId, props?: unknown): void {
    if (this.stack.length === 1 && this.current === id) {
      this.stack[0].screen.onShow?.(props);
      return;
    }
    while (this.stack.length > 0) this.hideTop();
    this.push(id, props);
  }

  /** Shows `id` on top of the current screen (modals, pause). */
  show(id: ScreenId, props?: unknown): void {
    if (this.current === id) {
      this.stack[this.stack.length - 1].screen.onShow?.(props);
      return;
    }
    // Showing a screen that is already lower in the stack pops back to it.
    const at = this.stack.findIndex((e) => e.id === id);
    if (at >= 0) {
      while (this.stack.length > at + 1) this.hideTop();
      this.reveal(this.stack[at]);
      return;
    }
    this.push(id, props);
  }

  /** Closes the top screen; the root screen stays. A modal pops away (scale down + fade). */
  back(): void {
    if (this.stack.length <= 1) return;
    this.hideTop(true);
    this.reveal(this.stack[this.stack.length - 1]);
    this.hooks.sound?.('back');
  }

  /** Pops every screen above `id` (no-op if `id` is not in the stack). */
  popTo(id: ScreenId): void {
    const at = this.stack.findIndex((e) => e.id === id);
    if (at < 0 || at === this.stack.length - 1) return;
    while (this.stack.length > at + 1) this.hideTop();
    this.reveal(this.stack[at]);
  }

  private reveal(top: Entry): void {
    if (this.closing.has(top.screen.el)) this.finishClose(top.screen.el);
    top.screen.el.hidden = false;
    top.screen.el.inert = false;
    const last = top.lastFocus;
    if (last && last.isConnected && top.screen.el.contains(last) && visible(last)) last.focus({ preventScroll: true });
    else this.focusFirst(top.screen.el);
    this.bus.emit('ui:screen', top.id);
  }

  private push(id: ScreenId, props: unknown): void {
    const screen = this.screens.get(id);
    if (!screen) throw new Error(`Unknown screen ${id}`);
    const below = this.stack[this.stack.length - 1];
    if (below) {
      const active = document.activeElement;
      below.lastFocus = active instanceof HTMLElement && below.screen.el.contains(active) ? active : null;
      // Modals keep the screen below visible behind their backdrop (inert: no focus, no clicks);
      // overlays keep it live.
      if (screen.el.classList.contains('modal')) below.screen.el.inert = true;
      else if (!screen.el.classList.contains('overlay')) below.screen.el.hidden = true;
    }
    if (this.closing.has(screen.el)) this.finishClose(screen.el); // reopened mid-exit
    this.stack.push({ id, screen, lastFocus: null });
    this.root.append(screen.el); // (re)append: later screens draw on top
    screen.el.hidden = false;
    screen.el.inert = false;
    screen.onShow?.(props);
    const panel = screen.el.querySelector<HTMLElement>(':scope > .springy, :scope > * > .springy, :scope > .panel');
    if (panel && screen.el.classList.contains('modal')) spring(panel, 0.9, 1);
    this.focusFirst(screen.el);
    this.bus.emit('ui:screen', id);
  }

  private hideTop(animate = false): void {
    const top = this.stack.pop();
    if (!top) return;
    const el = top.screen.el;
    if (animate && el.classList.contains('modal') && !reducedMotion()) {
      // It leaves the stack now (focus, input and logic move on at once) and is hidden once its
      // exit has played; inert meanwhile, so nothing in it takes focus or clicks.
      el.inert = true;
      el.classList.add('closing');
      this.closing.set(el, window.setTimeout(() => this.finishClose(el), CLOSE_MS));
    } else {
      this.finishClose(el);
    }
    top.screen.onHide?.();
  }

  private finishClose(el: HTMLElement): void {
    const t = this.closing.get(el);
    if (t !== undefined) clearTimeout(t);
    this.closing.delete(el);
    el.classList.remove('closing');
    el.hidden = true;
    el.inert = false;
  }

  /** Focuses the current screen's first control (keyboard users coming back to a menu). */
  focusCurrent(): void {
    const top = this.topEntry();
    if (top) this.focusFirst(top.screen.el);
  }

  /**
   * Focuses the screen's `[data-autofocus]` or first focusable element. In-world screens (lobby,
   * HUD, podium) carry `data-nofocus`: arrow keys must keep moving the bean there (§9.1).
   */
  focusFirst(scope: HTMLElement): void {
    const target = scope.hasAttribute('data-nofocus') ? null
      : [...scope.querySelectorAll<HTMLElement>('[data-autofocus]')].find(visible)
        ?? focusables(scope).find((n) => !n.classList.contains('close-x')) ?? null;
    if (target) target.focus({ preventScroll: true });
    else if (document.activeElement instanceof HTMLElement && this.root.contains(document.activeElement)) document.activeElement.blur();
  }

  private topEntry(): Entry | null {
    return this.stack[this.stack.length - 1] ?? null;
  }

  private focusedIn(scope: HTMLElement): HTMLElement | null {
    const a = document.activeElement;
    return a instanceof HTMLElement && scope.contains(a) ? a : null;
  }

  private nav(action: NavAction): void {
    const top = this.topEntry();
    if (!top || top.screen.el.hidden) return;
    if (top.screen.onNav?.(action)) return;
    const active = this.focusedIn(top.screen.el);
    switch (action) {
      case 'left': case 'right': {
        const adj = active?.closest<HTMLElement>('[data-adjust]');
        if (adj) {
          adj.dispatchEvent(new CustomEvent(ADJUST_EVENT, { detail: { dir: action === 'left' ? -1 : 1 } }));
          return;
        }
        this.moveFocus(top, action);
        return;
      }
      case 'up': case 'down': {
        const spin = active?.closest<HTMLElement>('[data-spin]');
        if (spin) {
          spin.dispatchEvent(new CustomEvent(SPIN_EVENT, { detail: { dir: action === 'up' ? 1 : -1 } }));
          return;
        }
        this.moveFocus(top, action);
        return;
      }
      case 'confirm':
        if (active) {
          if (active instanceof HTMLInputElement) active.form?.requestSubmit();
          else active.click();
        }
        return;
      case 'back':
        if (active && top.screen.el.hasAttribute('data-nofocus')) {
          active.blur();
          return;
        }
        if (top.screen.el.hasAttribute('data-noback')) return;
        this.back();
        return;
      default:
        return;
    }
  }

  /** Spatial navigation: the nearest focusable whose center lies in the pressed direction. */
  private moveFocus(top: Entry, dir: 'up' | 'down' | 'left' | 'right'): void {
    const items = focusables(top.screen.el);
    if (items.length === 0) return;
    const active = this.focusedIn(top.screen.el);
    if (!active || !items.includes(active)) {
      this.focus(items.find((n) => n.hasAttribute('data-autofocus')) ?? items[0]);
      return;
    }
    const a = active.getBoundingClientRect();
    const ax = a.left + a.width / 2;
    const ay = a.top + a.height / 2;
    const horizontal = dir === 'left' || dir === 'right';
    let best: HTMLElement | null = null;
    let bestScore = Infinity;
    for (const n of items) {
      if (n === active) continue;
      const r = n.getBoundingClientRect();
      const dx = r.left + r.width / 2 - ax;
      const dy = r.top + r.height / 2 - ay;
      const along = dir === 'left' ? -dx : dir === 'right' ? dx : dir === 'up' ? -dy : dy;
      if (along <= 4) continue;
      // Prefer targets that overlap the current one across the travel axis (same row/column).
      const overlap = horizontal
        ? Math.min(a.bottom, r.bottom) - Math.max(a.top, r.top)
        : Math.min(a.right, r.right) - Math.max(a.left, r.left);
      const across = horizontal ? Math.abs(dy) : Math.abs(dx);
      const score = along + (overlap > 0 ? across * 0.3 : across * 2.2 + 60);
      // Near-ties go to the earlier control in reading order (the left one of a row).
      if (score < bestScore - 6) {
        bestScore = score;
        best = n;
      }
    }
    if (!best && !horizontal) {
      // Wrap vertical lists: jump to the far end in the same column.
      let far: HTMLElement | null = null;
      let farScore = -Infinity;
      let farLeft = Infinity;
      for (const n of items) {
        if (n === active) continue;
        const r = n.getBoundingClientRect();
        const overlap = Math.min(a.right, r.right) - Math.max(a.left, r.left);
        if (overlap <= 0) continue;
        const dy = r.top + r.height / 2 - ay;
        const s = dir === 'down' ? -dy : dy;
        // Controls of one row (a name field and its dice) tie: the first in reading order wins.
        if (s > farScore + 12 || (s > farScore - 12 && r.left < farLeft)) {
          farScore = Math.max(s, farScore);
          farLeft = r.left;
          far = n;
        }
      }
      best = far;
    }
    if (best) this.focus(best);
  }

  private focus(n: HTMLElement): void {
    n.focus({ preventScroll: true });
    n.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    this.hooks.sound?.('hover');
  }
}
